/**
 * StintSense — Live Feed Zustand Store
 * Manages WebSocket connection to /api/live/feed for real-time team events.
 *
 * Two surfaces ride the one socket:
 *  - `events`: the discrete event log (lap_completed / run_completed) — LiveFeed.tsx.
 *  - `drivers`: the live multi-driver board (#291) — a map of consenting teammates
 *    to their latest `telemetry` frame. A `consent_revoked` control frame drops a
 *    driver immediately; otherwise a tile goes stale ~5s after the last frame
 *    (the backend cut is TTL-bounded, so the feed can end mid-stream — we never
 *    assume frames stop cleanly). Inputs are read ONLY from the sanctioned
 *    `frame.inputs` key the server controls — never an alternate/nested key.
 */

import { create } from "zustand";

export interface LiveEvent {
  id: string;
  type: "lap_completed" | "run_completed" | "connected" | "error" | "pong";
  teamId?: string;
  userId?: string;
  displayName?: string;
  lapNum?: number;
  lapTime?: number;
  setupName?: string;
  runId?: string;
  bestLap?: number;
  avgLap?: number;
  greenLaps?: number;
  message?: string;
  timestamp?: string;
  isPersonalBest?: boolean;
  receivedAt: number;
}

/** A consenting teammate's latest live frame (server-redacted). Matches the desktop
 *  CondensedFrame the relay publishes at ~1 Hz (camelCase). `inputs` is forward-
 *  looking — present only when the driver granted share_inputs AND the deferred
 *  desktop input wiring lands; absent today. The index signature keeps the frame
 *  loosely typed (the server controls its exact shape). */
export interface DriverFrame {
  position?: number;
  classPosition?: number;
  lap?: number;
  lapCompleted?: number;
  fuelLevelL?: number;
  fuelPct?: number;
  lapsRemainingAvg?: number | null;
  lapsRemainingWorst?: number | null;
  fuelPerLapAvgL?: number | null;
  onPitRoad?: boolean;
  isGreen?: boolean;
  isCaution?: boolean;
  sessionLapsRemainEx?: number;
  /** Present only when share_inputs was granted (deferred desktop wiring). */
  inputs?: { thr?: number; brk?: number; str?: number };
}

export interface DriverLive {
  userId: string;
  displayName: string;
  frame: DriverFrame;
  lastSeen: number;
}

interface LiveState {
  ws: WebSocket | null;
  connected: boolean;
  subscribedTeamId: string | null;
  events: LiveEvent[];
  /** Live board: userId -> latest frame from a consenting teammate. */
  drivers: Record<string, DriverLive>;

  connect: (teamId: string) => void;
  disconnect: () => void;
  clearEvents: () => void;
}

const MAX_EVENTS = 50;
/** A driver tile goes stale this long after its last frame. Forgiving enough for the
 *  ~1 Hz relay (drops only after several missed frames / a real disconnect); the
 *  consent-revoke drop is instant via the control frame, so this is only the
 *  stopped-publishing / disconnected backstop. */
const DRIVER_STALE_MS = 5000;

let _eventSeq = 0;
function nextEventId(): string {
  return `live-${Date.now()}-${++_eventSeq}`;
}

function buildWsUrl(teamId: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/live/feed?team_id=${teamId}`;
}

function reconnectDelay(attempt: number): number {
  const base = Math.min(1000 * Math.pow(2, attempt), 30_000);
  return base + Math.random() * 1000;
}

let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _reconnectAttempt = 0;
let _intentionalClose = false;
let _pingInterval: ReturnType<typeof setInterval> | null = null;
let _staleInterval: ReturnType<typeof setInterval> | null = null;
let _connGen = 0;

function _clearTimers() {
  if (_pingInterval) { clearInterval(_pingInterval); _pingInterval = null; }
  if (_staleInterval) { clearInterval(_staleInterval); _staleInterval = null; }
}

// SINGLE-CONSUMER assumption: this store holds ONE WebSocket with no refcount.
// connect()/disconnect() are designed for exactly one mounted consumer per route
// (today: CrewChief at /teams/:teamId/live). If a SECOND consumer (e.g. the parked
// TeamLiveBoard) is ever mounted alongside it, the first to unmount calls
// disconnect() and tears down the shared socket + wipes drivers{} for the
// survivor — add a small mount refcount before doing that.
export const useLiveStore = create<LiveState>((set, get) => ({
  ws: null,
  connected: false,
  subscribedTeamId: null,
  events: [],
  drivers: {},

  connect: (teamId: string) => {
    const state = get();
    if (state.ws && state.subscribedTeamId === teamId && state.connected) {
      return;
    }
    if (state.ws) {
      get().disconnect();
    }

    const gen = ++_connGen;
    _intentionalClose = false;
    _reconnectAttempt = 0;

    function createConnection() {
      if (_connGen !== gen) return;

      const url = buildWsUrl(teamId);
      const ws = new WebSocket(url);

      ws.onopen = () => {
        if (_connGen !== gen) { ws.close(); return; }
        _reconnectAttempt = 0;
        set({ connected: true, subscribedTeamId: teamId });

        _pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30_000);

        // Prune stale driver tiles (a feed can end mid-stream; the board cut is
        // TTL-bounded on the server, so we don't assume clean frame-stop).
        _staleInterval = setInterval(() => {
          const cutoff = Date.now() - DRIVER_STALE_MS;
          set((prev) => {
            let changed = false;
            const next: Record<string, DriverLive> = {};
            for (const [uid, d] of Object.entries(prev.drivers)) {
              if (d.lastSeen >= cutoff) next[uid] = d;
              else changed = true;
            }
            return changed ? { drivers: next } : {};
          });
        }, 1000);
      };

      ws.onmessage = (event) => {
        if (_connGen !== gen) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === "pong") return;

          // Live board: a consenting teammate's frame.
          if (data.type === "telemetry" && data.userId) {
            set((prev) => ({
              drivers: {
                ...prev.drivers,
                [data.userId]: {
                  userId: data.userId,
                  displayName: data.displayName || "Driver",
                  frame: (data.frame as DriverFrame) || {},
                  lastSeen: Date.now(),
                },
              },
            }));
            return;
          }
          // A driver revoked live-share -> drop them immediately.
          if (data.type === "consent_revoked" && data.userId) {
            set((prev) => {
              if (!prev.drivers[data.userId]) return {};
              const next = { ...prev.drivers };
              delete next[data.userId];
              return { drivers: next };
            });
            return;
          }

          // Otherwise it's a discrete event for the log.
          const liveEvent: LiveEvent = { id: nextEventId(), ...data, receivedAt: Date.now() };
          set((prev) => ({ events: [liveEvent, ...prev.events].slice(0, MAX_EVENTS) }));
        } catch {
          /* ignore malformed messages */
        }
      };

      ws.onclose = (event) => {
        if (_connGen !== gen) return;
        set({ connected: false });
        _clearTimers();
        if (!_intentionalClose && event.code < 4000) {
          _reconnectTimer = setTimeout(() => {
            if (_connGen !== gen || _intentionalClose) return;
            _reconnectAttempt++;
            createConnection();
          }, reconnectDelay(_reconnectAttempt));
        }
      };

      ws.onerror = () => {
        // onclose will fire after this, handling reconnect
      };

      set({ ws });
    }

    createConnection();
  },

  disconnect: () => {
    _connGen++;
    _intentionalClose = true;
    if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
    _clearTimers();

    const { ws } = get();
    if (ws) ws.close(1000, "Client disconnect");

    set({ ws: null, connected: false, subscribedTeamId: null, drivers: {} });
  },

  clearEvents: () => {
    set({ events: [] });
  },
}));
